/**
 * Shared Roadmap model for Main Command and the Inspector (#280).
 *
 * One source of truth: brunel.roadmap_items (read via reader, mutate via
 * upsert_roadmap_item / delete_roadmap_item). Main Command's get_roadmap and
 * this Inspector tool both project the same rows — never a second store.
 */

export type RoadmapHorizon = 'current' | 'next' | 'later';
export type RoadmapStatus = 'open' | 'in_progress' | 'blocked' | 'done' | 'dropped';

export interface RoadmapItem {
  readonly org_id: string;
  readonly id: string;
  readonly title: string;
  readonly summary: string;
  readonly horizon: RoadmapHorizon;
  readonly status: RoadmapStatus;
  readonly rank: number;
  readonly project_id: string | null;
  readonly project_name: string | null;
  readonly owner_label: string | null;
  readonly source_command_id: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export const ROADMAP_HORIZONS: readonly { id: RoadmapHorizon; label: string }[] = [
  { id: 'current', label: 'Current' },
  { id: 'next', label: 'Next' },
  { id: 'later', label: 'Later' },
] as const;

export const ROADMAP_STATUSES: readonly { id: RoadmapStatus; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'blocked', label: 'Blocked' },
  { id: 'done', label: 'Done' },
  { id: 'dropped', label: 'Dropped' },
] as const;

export function isRoadmapHorizon(value: unknown): value is RoadmapHorizon {
  return value === 'current' || value === 'next' || value === 'later';
}

export function normalizeRoadmapTitle(value: string): string {
  return value.trim().replace(/\s+/g, ' ').slice(0, 200);
}

export function normalizeRoadmapSummary(value: string | null | undefined): string {
  return (value ?? '').trim().replace(/\s+/g, ' ').slice(0, 2000);
}

/** Group + sort for the Inspector list. */
export function groupRoadmapItems(items: readonly RoadmapItem[]): Record<RoadmapHorizon, RoadmapItem[]> {
  const groups: Record<RoadmapHorizon, RoadmapItem[]> = { current: [], next: [], later: [] };
  const sorted = [...items].sort((a, b) => {
    if (a.rank !== b.rank) return a.rank - b.rank;
    return a.created_at.localeCompare(b.created_at);
  });
  for (const item of sorted) groups[item.horizon].push(item);
  return groups;
}

export function filterRoadmapItems(
  items: readonly RoadmapItem[],
  query: string,
  status: RoadmapStatus | 'all' = 'all',
): RoadmapItem[] {
  const q = query.trim().toLowerCase();
  return items.filter((item) => {
    if (status !== 'all' && item.status !== status) return false;
    if (q === '') return true;
    const hay = [
      item.title,
      item.summary,
      item.project_name ?? '',
      item.owner_label ?? '',
      item.horizon,
      item.status,
      String(item.rank),
    ].join(' ').toLowerCase();
    return hay.includes(q);
  });
}

export function nextRoadmapRank(items: readonly RoadmapItem[], horizon: RoadmapHorizon): number {
  let max = 0;
  for (const item of items) {
    if (item.horizon === horizon && item.rank > max) max = item.rank;
  }
  return max + 10;
}

export function bumpRoadmapRank(rank: number, direction: 'up' | 'down'): number {
  return direction === 'up' ? Math.max(0, rank - 10) : rank + 10;
}

export type RoadmapOwnerIntent =
  | { readonly kind: 'list' }
  | { readonly kind: 'add'; readonly title: string; readonly horizon: RoadmapHorizon }
  | { readonly kind: 'mark_done'; readonly match: string }
  | { readonly kind: 'delete'; readonly match: string }
  | { readonly kind: 'move_higher'; readonly match: string }
  | { readonly kind: 'set_horizon'; readonly match: string; readonly horizon: RoadmapHorizon };

/**
 * Lightweight owner-phrasing detector so Main Command composer and the
 * Inspector share the same mutation vocabulary against the same store.
 */
export function parseRoadmapOwnerIntent(text: string): RoadmapOwnerIntent | null {
  const raw = text.trim().replace(/\s+/g, ' ');
  if (raw === '') return null;
  const lower = raw.toLowerCase();

  if (/^(show|list|open)\s+(the\s+)?roadmap\b/.test(lower) || lower === 'roadmap') {
    return { kind: 'list' };
  }

  const add = /^(?:add|put)\s+(?:this\s+)?(?:to\s+(?:the\s+)?roadmap[:\s]+)?(.+?)(?:\s+as\s+(current|next|later))?$/i.exec(raw)
    ?? /^add\s+to\s+(?:the\s+)?roadmap[:\s]+(.+)$/i.exec(raw);
  if (add) {
    let title = normalizeRoadmapTitle(add[1] ?? '');
    title = title.replace(/\s+to\s+(the\s+)?roadmap$/i, '').trim();
    if (title === '') return null;
    const horizonRaw = (add[2] ?? 'next').toLowerCase();
    const horizon = isRoadmapHorizon(horizonRaw) ? horizonRaw : 'next';
    return { kind: 'add', title, horizon };
  }

  const mark = /^(?:mark|set)\s+(.+?)\s+(?:as\s+)?done$/i.exec(raw);
  if (mark) return { kind: 'mark_done', match: normalizeRoadmapTitle(mark[1] ?? 'this') };

  const del = /^(?:delete|remove)\s+(?:this\s+)?(?:roadmap\s+item\s+)?(.+)$/i.exec(raw);
  if (del) return { kind: 'delete', match: normalizeRoadmapTitle(del[1] ?? '') };

  const higher = /^(?:move|raise|bump)\s+(.+?)\s+higher$/i.exec(raw)
    ?? /^move\s+this\s+higher$/i.exec(raw);
  if (higher) return { kind: 'move_higher', match: normalizeRoadmapTitle(higher[1] ?? 'this') };

  const horizonMove = /^(?:move|put)\s+(.+?)\s+(?:to\s+)?(current|next|later)$/i.exec(raw);
  if (horizonMove) {
    return {
      kind: 'set_horizon',
      match: normalizeRoadmapTitle(horizonMove[1] ?? ''),
      horizon: horizonMove[2]!.toLowerCase() as RoadmapHorizon,
    };
  }

  return null;
}

export function findRoadmapMatch(items: readonly RoadmapItem[], match: string): RoadmapItem | null {
  const q = match.trim().toLowerCase();
  if (q === '' || q === 'this') return items[0] ?? null;
  const exact = items.find((item) => item.title.toLowerCase() === q);
  if (exact) return exact;
  return items.find((item) => item.title.toLowerCase().includes(q)) ?? null;
}
